<template>
  <div
    v-if="props.editor"
    class="
      tw-p-1
      tw-px-2
      tw-border
      tw-border-solid
      tw-border-sf-snow-darker
      tw-border-t-0
      tw-border-b-1
      tw-border-l-0
      tw-border-r-0
      tw-flex
      tw-flex-wrap
      tw-gap-1
      tw-items-center
      tw-bg-sf-snow
      tw-rounded-t
    "
  >
    <!-- Mention -->
    <button
      v-if="!props.hideMention"
      type="button"
      class="
        fa fa-at
        tw-w-6
        tw-h-6
        tw-outline
        tw-outline-1
        tw-outline-solid
        tw-outline-sf-snow-darker
      "
      data-heap="markdown-editor-on-mention-click"
      :class="buttonClasses"
      :title="translation.mention"
      @click="onMentionClick"
    ></button>

    <!-- Documents & Exports -->
    <button
      v-if="!props.hideLinking"
      type="button"
      class="
        !tw-p-0
        tw-w-6
        tw-h-6
        tw-font-bold
        tw-outline
        tw-outline-1
        tw-outline-solid
        tw-outline-sf-snow-darker
      "
      data-heap="markdown-editor-on-slash-click"
      :class="buttonClasses"
      :title="translation.slash"
      @click="onDocumentLinkClick"
    >
      /
    </button>

    <!-- Visual separator for button groups -->
    <div
      class="tw-self-stretch tw-w-px tw-bg-sf-snow-darker tw-mx-1 -tw-my-1"
      aria-hidden="true"
    ></div>

    <!-- Bold -->
    <button
      type="button"
      class="fa fa-bold"
      data-heap="markdown-editor-toggle-bold"
      :disabled="!canToggleBold"
      :class="boldButtonClasses"
      :title="translation.bold"
      @click="toggleBold"
    ></button>

    <!-- Italic -->
    <button
      type="button"
      class="fas fa-italic"
      data-heap="markdown-editor-toggle-italic"
      :disabled="!canToggleItalic"
      :class="italicButtonClasses"
      :title="translation.italic"
      @click="toggleItalic"
    ></button>

    <!-- Link -->
    <button
      type="button"
      class="fa fa-link"
      data-heap="markdown-editor-handle-link"
      :disabled="!canHandleLink"
      :class="linkButtonClasses"
      :title="translation.link"
      @click="handleLink"
    ></button>

    <!-- Bullet list -->
    <button
      type="button"
      class="fa fa-list-ul"
      data-heap="markdown-editor-toggle-bullet-list"
      :class="bulletListButtonClasses"
      :title="translation.bulletList"
      @click="props.editor.chain().focus().toggleBulletList().run()"
    ></button>

    <!-- Ordered list -->
    <button
      type="button"
      class="fa fa-list-ol"
      data-heap="markdown-editor-toggle-ordered-list"
      :class="orderedListButtonClasses"
      :title="translation.orderedList"
      @click="props.editor.chain().focus().toggleOrderedList().run()"
    ></button>

    <!-- Visual separator for button groups -->
    <div
      class="tw-self-stretch tw-w-px tw-bg-sf-snow-darker tw-mx-1 -tw-my-1"
      aria-hidden="true"
    ></div>

    <!-- Undo -->
    <button
      type="button"
      class="fa fa-undo"
      data-heap="markdown-editor-undo"
      :disabled="!props.editor.can().chain().focus().undo().run()"
      :class="buttonClasses"
      :title="translation.undo"
      @click="props.editor.chain().focus().undo().run()"
    ></button>

    <!-- Redo -->
    <button
      type="button"
      class="fa fa-redo"
      data-heap="markdown-editor-redo"
      :disabled="!props.editor.can().chain().focus().redo().run()"
      :class="buttonClasses"
      :title="translation.redo"
      @click="props.editor.chain().focus().redo().run()"
    ></button>
  </div>

  <!-- Show a popover when a link is being edited -->
  <div
    v-if="editingLink"
    class="
      tw-p-3
      tw-flex
      tw-gap-2
      tw-bg-sf-snow-light
      tw-items-center
      tw-align-center
    "
  >
    <label for="editor-popover-link" class="tw-text-sm tw-mb-0">URL</label>

    <SfInput
      id="editor-popover-link"
      v-model="url"
      type="text"
      :placeholder="'https://example.com'"
      @keydown.enter.prevent="setLink"
      @keydown.esc="editingLink = false"
    />

    <button
      class="sf-btn sf-btn-small fa fa-remove"
      @click="editingLink = false"
    ></button>
    <button class="sf-btn sf-btn-small fa fa-check" @click="setLink"></button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Editor } from '@tiptap/core'
import SfInput from '@app/shared/vue/components/sf-input/sf-input.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

interface Props {
  editor: Editor
  hideLinking?: boolean
  hideMention?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hideLinking: false,
  hideMention: false,
})
const I18n = useI18n()
const { editor } = props
const editingLink = ref(false)
const url = ref('')
const insertChar = (char: string) => {
  if (!editor) return

  const { selection } = editor.state
  editor.commands.insertContentAt(selection.from, char)
  editor.view.focus()
}

const onMentionClick = () => {
  insertChar('@')
}

const onDocumentLinkClick = () => {
  insertChar('/')
}

const canToggleBold = computed(() => {
  if (!editor) return false
  const { selection } = editor.state

  if (selection.empty) {
    return editor.can().chain().focus().selectWord().toggleBold().run()
  }

  return editor.can().chain().focus().toggleBold().run()
})

const toggleBold = () => {
  if (!editor) return
  const { selection } = editor.state

  if (selection.empty) {
    return editor.chain().focus().selectWord().toggleBold().run()
  }

  return editor.chain().focus().toggleBold().run()
}

const canToggleItalic = computed(() => {
  if (!editor) return false
  const { selection } = editor.state

  if (selection.empty) {
    return editor.can().chain().focus().selectWord().toggleItalic().run()
  }

  return editor.can().chain().focus().toggleItalic().run()
})

const toggleItalic = () => {
  if (!editor) return
  const { selection } = editor.state

  if (selection.empty) {
    return editor.chain().focus().selectWord().toggleItalic().run()
  }

  return editor.chain().focus().toggleItalic().run()
}

const canHandleLink = computed(() => {
  if (!editor) return false

  return true
})

const setLink = () => {
  editingLink.value = false

  if (!editor) return
  let href = url.value

  if (href === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  const { selection } = editor.state

  // Prepend https:// if the URL doesn't contain a protocol
  if (!href.match('://')) {
    href = `https://${href}`
  }

  if (selection.empty) {
    return editor
      .chain()
      .focus()
      .selectWord()
      .setLink({ href })
      .updateAttributes('link', { title: href })
      .run()
  }

  return editor
    .chain()
    .focus()
    .setLink({ href })
    .updateAttributes('link', { title: href })
    .run()
}

const handleLink = () => {
  if (!editor) return

  if (editor.isActive('link')) {
    editor.chain().focus().unsetLink().run()
  } else {
    url.value = ''
    editingLink.value = true
  }
}

const isActive = (name: string) => editor?.isActive(name)
const isMac = navigator.userAgent.toUpperCase().includes('MAC')

const modifierKey = isMac ? '⌘' : 'Ctrl+'
const shiftKey = modifierKey === '⌘' ? '⇧' : 'Shift+'

const translation = {
  mention: I18n.t('components.markdown_editor.toolbar.mention', {
    shortcut: '@',
  }),
  slash: I18n.t('components.markdown_editor.toolbar.slash', {
    shortcut: '/',
  }),
  bold: I18n.t('components.markdown_editor.toolbar.bold', {
    shortcut: `${modifierKey}B`,
  }),
  italic: I18n.t('components.markdown_editor.toolbar.italic', {
    shortcut: `${modifierKey}I`,
  }),
  link: I18n.t('components.markdown_editor.toolbar.link'),
  bulletList: I18n.t('components.markdown_editor.toolbar.bulletList', {
    shortcut: `${modifierKey}${shiftKey}8`,
  }),
  orderedList: I18n.t('components.markdown_editor.toolbar.orderedList', {
    shortcut: `${modifierKey}${shiftKey}7`,
  }),
  undo: I18n.t('components.markdown_editor.toolbar.undo', {
    shortcut: `${modifierKey}Z`,
  }),
  redo: I18n.t('components.markdown_editor.toolbar.redo', {
    shortcut: `${modifierKey}${shiftKey}Z`,
  }),
}

const buttonClasses =
  'tw-rounded tw-p-1.5 tw-bg-transparent tw-border-none tw-shadow-none tw-text-sf-neutral-dark-1 hover:tw-bg-sf-snow-dark'

function formatButtonClass(format: string) {
  return computed(() => {
    const enabled = isActive(format)
    return [buttonClasses, { 'tw-text-sf-primary': enabled }]
  })
}

const boldButtonClasses = formatButtonClass('bold')
const italicButtonClasses = formatButtonClass('italic')
const linkButtonClasses = formatButtonClass('link')
const bulletListButtonClasses = formatButtonClass('bulletList')
const orderedListButtonClasses = formatButtonClass('orderedList')
</script>

<style scoped>
button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
